import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LinearScale,
  CategoryScale,
  BarElement,
  type ChartOptions,
  TooltipItem,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useEffect, useState } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';

import { AnimatedCounter } from '@components/AnimatedCounter';
import {
  Counter,
  CounterContainer,
  Graphs,
  GraphsContainer,
} from '@components/Container';
import { DashboardItemChart } from '@components/DashboardItemChart';
import { Loading } from '@components/Loading';
import { DashboardService } from '@services/Dashboard';
import { formatAsCurrency } from '@utils/numberFormat';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  LinearScale,
  BarElement,
  CategoryScale,
  ChartDataLabels
);

type Meta = 'counts' | 'amounts';

const initialChartData = (meta: Meta): any => ({
  labels: ['Post', 'Email Sent', 'Posted'],
  datasets: [
    {
      data: [0, 0, 0],
      backgroundColor: ['#36b9cc', '#58d968', '#ff914d'],
      borderWidth: 0,
      cutout: '60%',
      meta: meta,
    },
  ],
});

const DataCounters = [
  {
    title: 'Estimates',
    description: 'Quotations for clients.',
  },
  {
    title: 'Invoices',
    description: 'Billed to clients.',
  },
  {
    title: 'Customers',
    description: 'Active clients.',
  },
  {
    title: 'Products',
    description: 'Available for sale.',
  },
];

const defaultInvoiceTotals = {
  Post: 0,
  EmailSent: 0,
  Posted: 0,
};

const ItemCharts = [
  { status: 'post' },
  { status: 'email-sent' },
  { status: 'posted' },
];

export const DashboardItems = () => {
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [estimateCount, setEstimateCount] = useState(0);
  const [customerCount, setCustomerCount] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [isLoadingSummaryStats, setIsLoadingSummaryStats] = useState(true);

  const counters: Record<string, number> = {
    Estimates: estimateCount,
    Invoices: invoiceCount,
    Customers: customerCount,
    Products: productCount,
  };

  const [invoiceChartData, setInvoiceChartData] = useState<any>(
    initialChartData('counts')
  );

  const [invoiceChartAmount, setInvoiceChartAmount] = useState<any>(
    initialChartData('amounts')
  );

  const [invoiceTotals, setInvoiceTotals] = useState({
    ...defaultInvoiceTotals,
  });

  const [monthlyInvoiceData, setMonthlyInvoiceData] = useState<number[]>([]);

  const fetchSummaryStats = async () => {
    setIsLoadingSummaryStats(true);
    try {
      const result = await DashboardService.getSummaryStats();
      if ('data' in result) {
        setInvoiceCount(result.data.invoiceCount || 0);
        setEstimateCount(result.data.estimateCount || 0);
        setCustomerCount(result.data.customerCount || 0);
        setProductCount(result.data.productCount || 0);
      }
    } catch (error) {
      console.error('Error fetching summary stats:', error);
    } finally {
      setIsLoadingSummaryStats(false);
    }
  };

  const fetchInvoiceStatusCounts = async () => {
    const statsResult = await DashboardService.getInvoiceStats();
    if (!('data' in statsResult)) return;

    const { countByStatus, totalByStatus } = statsResult.data;

    const statusCounts = { ...defaultInvoiceTotals };

    const statusTotals = { ...defaultInvoiceTotals };

    // Map API response to our status structure
    countByStatus.forEach((item) => {
      switch (item.status.toLowerCase()) {
        case 'post':
          statusCounts.Post = item.count;
          break;
        case 'email sent':
          statusCounts.EmailSent = item.count;
          break;
        case 'posted':
          statusCounts.Posted = item.count;
          break;
      }
    });

    totalByStatus.forEach((item) => {
      switch (item.status.toLowerCase()) {
        case 'post':
          statusTotals.Post = item.total;
          break;
        case 'email sent':
          statusTotals.EmailSent = item.total;
          break;
        case 'posted':
          statusTotals.Posted = item.total;
          break;
      }
    });

    setInvoiceChartData({
      ...initialChartData('counts'),
      datasets: [
        {
          ...initialChartData('counts'),
          data: [
            statusCounts.Post,
            statusCounts.EmailSent,
            statusCounts.Posted,
          ],
        },
      ],
    });

    setInvoiceChartAmount({
      ...initialChartData('amounts'),
      datasets: [
        {
          ...initialChartData('amounts'),
          data: [
            statusCounts.Post,
            statusCounts.EmailSent,
            statusCounts.Posted,
          ],
        },
      ],
    });

    setInvoiceTotals({ ...statusTotals });
  };

  const fetchInvoiceMonthlyStats = async () => {
    const result = await DashboardService.getInvoiceMonthlyStats();
    if (!('data' in result)) return;

    // Create array for the last 6 months with current month labels
    const monthlyTotals = Array(6).fill(0);
    const currentDate = new Date();

    // Create a map of month names to their totals from API
    const monthTotalsMap = new Map<string, number>();
    result.data.forEach((item) => {
      monthTotalsMap.set(item.month, item.total);
    });

    // Fill the array with data for the last 6 months
    for (let i = 0; i < 6; i++) {
      const monthDate = new Date();
      monthDate.setMonth(currentDate.getMonth() - (5 - i));
      const monthName = monthDate.toLocaleString('default', { month: 'short' });
      monthlyTotals[i] = monthTotalsMap.get(monthName) || 0;
    }

    setMonthlyInvoiceData(monthlyTotals);
  };

  useEffect(() => {
    fetchSummaryStats();
    fetchInvoiceStatusCounts();
    fetchInvoiceMonthlyStats();
  }, []);

  const invoiceOptions: ChartOptions<'doughnut'> = {
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          boxWidth: 20,
          padding: 15,
          font: { size: 13 },
        },
      },

      datalabels: {
        color: '#fff',
        formatter: (value: number, context) => {
          const dataset = context.dataset as any;
          const metaType = dataset.meta;
          const data = dataset.data as number[];
          const total = data.reduce((a, b) => a + b, 0);

          const allZero = data.every((v) => v === 0);
          if (!allZero && value === 0) return '';

          if (metaType === 'amounts') {
            return '';
          }
          const percentage = total === 0 ? 0 : (value / total) * 100;
          return `${Math.round(percentage)}%`;
        },
        font: (context) => {
          const dataset = context.chart.data.datasets[
            context.datasetIndex
          ] as any;
          const isAmount = dataset.meta === 'amounts';

          return {
            weight: 'bold',
            size: isAmount ? 13.5 : 16,
          };
        },
      },
    },
    maintainAspectRatio: false,
  };

  const barLabels = Array.from({ length: 6 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - (5 - i));
    return date.toLocaleString('default', { month: 'short' });
  });

  const barData = {
    labels: barLabels,
    datasets: [
      {
        data: monthlyInvoiceData,
        backgroundColor: '#4e73df',
      },
    ],
  };

  const barOptions: ChartOptions<'bar'> = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'bar'>) => {
            const raw = context.raw as number;
            return `$${raw.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
          },
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) =>
            Number(value).toLocaleString(undefined, {
              maximumFractionDigits: 0,
            }),
        },
      },
    },
  };

  const chartsAmount: Record<string, number> = {
    post: invoiceTotals.Post,
    'email-sent': invoiceTotals.EmailSent,
    posted: invoiceTotals.Posted,
  };

  return (
    <>
      <CounterContainer>
        {DataCounters.map((item, index) => (
          <Counter
            key={index}
            title={item.title}
            description={item.description}
          >
            {isLoadingSummaryStats ? (
              <div className="flex justify-center items-center h-12">
                <Loading />
              </div>
            ) : (
              <AnimatedCounter target={counters[item.title]} />
            )}
          </Counter>
        ))}
      </CounterContainer>

      <GraphsContainer>
        <Graphs>
          <div className="font-semibold lg:text-lg md:text-base text-sm md:text-left text-center">
            Invoice Count per status
          </div>
          <div className="md:flex hidden justify-center space-x-5 text-xs lg:h-3 h-4" />
          <div className="lg:h-60 md:h-52 h-48">
            <Doughnut data={invoiceChartData} options={invoiceOptions} />
          </div>
        </Graphs>

        <Graphs>
          <div className="font-semibold lg:text-lg md:text-base text-sm md:text-left text-center">
            Invoice Total per status
          </div>
          <div className="flex justify-center space-x-5 text-xs pt-1 md:pt-0">
            {ItemCharts.map((item, index) => (
              <DashboardItemChart key={index} status={item.status}>
                {formatAsCurrency(chartsAmount[item.status])}
              </DashboardItemChart>
            ))}
          </div>
          <div className="lg:h-60 md:h-52 h-48">
            <Doughnut data={invoiceChartAmount} options={invoiceOptions} />
          </div>
        </Graphs>

        <div className="lg:col-span-4 md:col-span-3 col-span-3 shadow-md bg-white p-5 lg:space-y-5 md:space-y-4 space-y-0.5 rounded-lg text-gray-600">
          <div className="font-semibold lg:text-lg md:text-base text-sm md:text-left text-center">
            Monthly Invoice Amount <br className="sm:hidden block" />
            (last 6 months)
          </div>
          <div className="h-36 md:h-auto">
            <Bar data={barData} options={barOptions} />
          </div>
        </div>
      </GraphsContainer>
    </>
  );
};
